import React, { useState, useEffect} from "react";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import Select, { components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import Icon from "components/Icons/Icon";
import Alert from "components/Common/Alert";
import conxtdOut from "apis/conxtdOut";
import { darkModeSelectStyles } from "components/Common/CustomStyles/darkModeSelectStyles";
import "./UpdateField.scss";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import { loadCompanyAssetsStart } from "services/redux/actions/serviceDesk";
import Loading from "components/Loading";

// Custom Select Component and Options -----
const CustomOption = (props) => {
  const { data } = props;

  if (!data?.label || !data?.value) { return null };

  return (
    <components.Option {...props}>
      <div className='d-flex align-items-center pl-1 pr-1'>
        <Icon style={{ color: data?.colour ?? 'lightgray' }} className='fas fa-circle fa-xs mr-2' />
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};
const CustomSingleValue = (props) => {
  const { data } = props;

  if (!data?.label || !data?.value) { return null };

  return (
    <components.SingleValue {...props}>
      <div className='d-flex align-items-center pl-1 pr-1'>
        <Icon style={{ color: data?.colour ?? 'lightgray' }} className='fas fa-circle fa-xs mr-2' />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};
// -----------

const AssetFieldModal = ({ 
  showAssetFieldModal, 
  setShowAssetFieldModal, 
  companyAssetsFields, 
  handleUpdateFields, 
  fieldToChange, 
  setFieldToChange, 
  setNewFieldValue,
  newFieldValue,
  selectedCount,
  loadingSubmission,
 }) => {

  const darkMode = useSelector(state => state.app.darkMode)

  const [assetFieldOptions, setAssetFieldOptions] = useState([]);
  const [updateDropdownOptions, setUpdateDropdownOptions] = useState([]);
  const [hasUpdateDropdownOptions, setHasUpdateDropdownOptions] = useState(false);
  const [fieldType, setFieldType] = useState('text');
  const [invalidSubmissionRequirements, setInvalidSubmissionRequirements] = useState(true);

  useEffect(() => {
    setInvalidSubmissionRequirements(!fieldToChange || !newFieldValue)
  }, [fieldToChange, newFieldValue])
  
  useEffect(() => {
    setHasUpdateDropdownOptions(updateDropdownOptions?.length > 0);
  }, [updateDropdownOptions])

  // builds the list of selectable fields only. The value options belong to
  // whichever field the user picks, so they're derived separately below.
  useEffect(() => {
    const options = companyAssetsFields?.map(field => {
      if (field?.type === 'file') { return null };

      return {
        label: field?.field_name,
        value: field?.field_uid,
        type: field?.type,
      }
    })
    ?.filter(f => f !== null);

    options && setAssetFieldOptions(options);
  }, [companyAssetsFields])

  // Options for the "new value" dropdown, taken from the selected field.
  // This used to be set inside the map above — once per field, so each
  // iteration overwrote the one before it and only the last field processed
  // survived (or an empty list, if that field wasn't a dropdown). The result
  // was a single shared set of options with no relation to the field actually
  // selected. Keying it to fieldToChange gives each dropdown field its own.
  useEffect(() => {
    const selectedField = companyAssetsFields?.find(f => f?.field_uid === fieldToChange);

    if (selectedField?.type !== 'dropdown') {
      setUpdateDropdownOptions([]);
      return;
    }

    setUpdateDropdownOptions(
      Object.values(selectedField?.options ?? {})?.map(option => ({
        label: option?.option,
        value: option?.option,
        colour: option?.colour,
      })) ?? []
    );
  }, [fieldToChange, companyAssetsFields])

  const handleClose = () => {
    setShowAssetFieldModal(false);
    setFieldToChange(null);
    setFieldType("text");
    setHasUpdateDropdownOptions(false);
  }

  const messageNuance = () => {
    switch (fieldType) {
      case 'dropdown':
        return 'Select';
    
      default:
        return "Enter"
    }
  }

  return (
    <Modal
      backdrop="static"    
      centered
      keyboard={true}
      onHide={() => handleClose()}
      show={showAssetFieldModal}
      size="lg"
      id="sd-update-asset-fields-modal"
    >
      <Modal.Header
        closeButton
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Modal.Title className="w-100">
          <Row className="d-flex align-items-center">
            <Col xs={6} md={6} lg={6} xl={6}>
              <span className="" style={{ fontSize: "0.7em" }}>Change Asset Field Value</span>
            </Col>
            <Col xs={6} md={6} lg={6} xl={6}>
              <div
                className="d-flex justify-content-end flex-row align-items-center flex-nowrap" 
                style={{ fontSize: "0.6em" }}
              >
                Assets Selected:
              <span className="font-weight-bold ml-1">{selectedCount}</span>
              </div>
            </Col>
          </Row>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className={`${darkMode ? "darkMode" : "background-modal-wrapper"}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Asset Field Selection */}
        <div className="section">
          <span>1. Select the asset field that you wish to change values for in your selection</span>
          <div className='d-flex flex-column'>
            <Select
              className="mt-2"
              menuPosition="fixed"
              options={assetFieldOptions}
              placeholder="Select an asset field to change..."
              maxMenuHeight={250}
              isClearable
              onChange={(e) => {
                setFieldToChange(e?.value ?? null);
                e?.type && setFieldType(e?.type ?? 'text');
              }}
              styles={{ ...(darkMode ? darkModeSelectStyles : {}) }}
            />
          </div>
        </div>

        {/* New Value Entry */}
        {!fieldToChange ? <></> :
          <div className="mt-3 section">
            <span>{`2. ${messageNuance()} the asset field that you wish to change values for in your selection`}</span>
            <div>
              {/* Loads a dropdown with options for if selected asset field option is of type: 'dropdown' */}
                <>
                  {/* dropdown */}
                  {hasUpdateDropdownOptions && fieldType === 'dropdown' &&
                    <div className='d-flex flex-column mt-2'>
                      <Select
                        menuPosition="fixed"
                        components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
                        options={updateDropdownOptions}
                        placeholder="Select a new value..."
                        isClearable
                        maxMenuHeight={250}
                        onChange={(e) => setNewFieldValue(e?.value)}
                        styles={{ ...(darkMode ? darkModeSelectStyles : {}) }}
                      />
                    </div>}
                
                  {/* text */}
                  {fieldType === 'text' &&
                    <div className='d-flex flex-column mt-1'>
                      <span className='mb-1'></span>
                      <Form.Control
                        name="newValueInputText"
                        type="text" 
                        placeholder="Enter a new value..."
                        onChange={(e) => setNewFieldValue(e.target.value)}
                      />
                    </div>
                  }
                </>
            </div>
          </div>
        }
      </Modal.Body>
      <Modal.Footer>
        {/* Close Button */}
        <Button
          data-intercom-target={"close-modal-button-add-site"}
          variant="primary"
          className="mr-1"
          onClick={() => handleClose()}
        >
          Close
        </Button>
        {/* Submit Button */}
        {invalidSubmissionRequirements ?
          <Tooltip
            placement="top"
            description={
              <ul className="m-0 pl-3 text-left">
                {!fieldToChange && <li key={'invalid-update-fields-submission'}>
                  <span className='font-weight-bold mr-1'>Asset Field</span>
                  - required
                </li>}
                {!newFieldValue && <li key={'invalid-update-fields-submission'}>
                  <span className='font-weight-bold mr-1'>New Value</span>
                  - required
                </li>}
              </ul>
            }
          >
            <Button
              data-intercom-target={"close-modal-button-add-site"}
              variant="success"
              disabled={!fieldToChange || !newFieldValue}
              onClick={() => handleUpdateFields()}
            >
            {loadingSubmission ? <Loading/> : "Submit"}
          </Button>
          </Tooltip>
        :
          <Button
            data-intercom-target={"close-modal-button-add-site"}
            variant="success"
            disabled={!fieldToChange || !newFieldValue}
            onClick={() => handleUpdateFields()}
          >
            {loadingSubmission ? <Loading/> : "Submit"}
          </Button>
        }
      </Modal.Footer>
    </Modal>
  )
}

const UpdateFields = ({ 
  showAssetFieldModal, 
  setShowAssetFieldModal, 
  companyAssetsFields, 
  selectedCount, 
  selection, 
  setSelection,
  selectAll,
  setSelectAll,
  checkboxPagination, 
  tableFilters,
  companyId,
  totalAssets,
 }) => {

  const dispatch = useDispatch();

  const darkMode = useSelector(state => state.app.darkMode)
  const [loadingSubmission, setLoadingSubmission] = useState(false);

  useEffect(() => {
    return () => {
      setShowAssetFieldModal(false);
    }
  }, [])

  const handleShowAssetFieldModal = () => {
    setShowAssetFieldModal(true);
  }

  // update fields
  const [fieldToChange, setFieldToChange] = useState(null);
  const [newFieldValue, setNewFieldValue] = useState(null);

  const handleUpdateFields = () => {
    const findAssetName = companyAssetsFields?.find(f => f?.field_uid === fieldToChange)?.field_name

    Alert({
      html: `
        <div class="">
          <div class="mb-1">
            <span class="">Field: </span>
            <span class="font-weight-bold">${findAssetName ?? ''}</span>
          </div>
          <div class="mb-1">
            <span class="">New Value: </span>
            <span class="font-weight-bold">${newFieldValue ?? ''}</span>
          </div>
          <span class="mt-1">Confirm changes for <span class="font-weight-bold">${selectedCount ?? ''}</span> asset${selectedCount > 1 ? 's' : ''}?</span>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {

        if (!companyId || !fieldToChange || !newFieldValue) { return; }

        setLoadingSubmission(true);
        const updateFields = conxtdOut.post("/company-assets-bulk/edit", {
          "asset_ids" : selection,
          "company_id": companyId,
          "select_all": selectAll,
          "field_uid": fieldToChange,
          "new_field_value": newFieldValue,
          ...checkboxPagination,
        })

        updateFields.then((response) => {
          setLoadingSubmission(false);
            Alert({
              text: "Assets modified successfully",
              icon: "success",
              timerProgressBar: true,
              timer: 5000,
            });

            setSelection([]);
            setSelectAll(false);

            dispatch(loadCompanyAssetsStart({ 
              companyId: companyId, 
              requestPageNumber: 1, // tells the back end which page to serve (starts at 1, like normal page numbering)
              pageSize: 100,
              filters: tableFilters ?? [],
              // externalFilters: externalFilters,
            }))
        }).catch((error) => {
          setLoadingSubmission(false);
          console.log("error", error.message);
          Alert({
            text: "Failed to modify assets, please try again later.",
            icon: "error",
            timerProgressBar: true,
            timer: 5000,
          });
        })
      }
    })
  }

  const tooltipHTML = () => (
    <div>
      Asset selection is currently limited to <span className="font-weight-bold">10,000</span> for this feature
    </div>
  )
  

  return (
    <>
      {totalAssets > 10000 ?
        <Tooltip
          description={tooltipHTML()}
          placement="top"
        >
          <Button
            disabled={totalAssets > 10000}
            variant={darkMode ? 'primary' : 'light'}
            type="submit"
            className="action-button"
            id="update-fields-button"
            onClick={() => handleShowAssetFieldModal()}
          >
            <Icon className="fas fa-sync-alt icons" />
            <span className="action-text">Update Fields</span>
          </Button>
        </Tooltip>
      :
        <Button
          disabled={totalAssets > 10000}
          variant={darkMode ? 'primary' : 'light'}
          type="submit"
          className="action-button"
          id="update-fields-button"
          onClick={() => handleShowAssetFieldModal()}
        >
          <Icon className="fas fa-sync-alt icons" />
          <span className="action-text">Update Fields</span>
        </Button>
      }

      {showAssetFieldModal && 
        <AssetFieldModal
          showAssetFieldModal={showAssetFieldModal}
          setShowAssetFieldModal={setShowAssetFieldModal}
          companyAssetsFields={companyAssetsFields}
          handleUpdateFields={handleUpdateFields}
          fieldToChange={fieldToChange}
          setFieldToChange={setFieldToChange}
          newFieldValue={newFieldValue}
          setNewFieldValue={setNewFieldValue}
          selectedCount={selectedCount}
          darkMode={darkMode}
          loadingSubmission={loadingSubmission}
          totalAssets={totalAssets}
        />
      }

    </>
  );
}

export default UpdateFields;
